"use client";

import { useConsent } from "@packages/consent";
import { useLocale, usePathname } from "@packages/i18n";
import {
  getCookieAjsAnonymousId,
  getDeviceIsMobile,
  getDeviceType,
  getFullName,
  useAccessibility,
  useRequestEvent,
  useRequestMe,
  uuid,
} from "@packages/sdk";
import { useSearchParams } from "next/navigation";
import type { ComponentProps } from "react";
import { Suspense, useCallback, useEffect, useState } from "react";

import { analytics, analyticsContext } from "../lib";
import type { AnalyticsEvent } from "../types";

export type AnalyticsProviderProps = Omit<
  ComponentProps<typeof analyticsContext.Provider>,
  "value"
>;

const Provider = ({ children, ...props }: AnalyticsProviderProps) => {
  const [isIdentified, setIsIdentified] = useState(false);

  const locale = useLocale();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const { hlwFontStyle, hlwLetterSpacing, hlwWordSpacing, preferredTheme } =
    useAccessibility();
  const { ajsAnonymousId, consent, isConsenting, setAjsAnonymousId } =
    useConsent();
  const { query: queryMe } = useRequestMe();
  const { mutationPost: postEvent } = useRequestEvent();

  const logout = () => analytics?.reset();
  const track = ({ event, properties }: AnalyticsEvent) => {
    const isWindowExists = typeof window !== "undefined";

    return analytics.track(event, {
      device_type: getDeviceType(),
      font_style: hlwFontStyle,
      is_anonymous: !isIdentified,
      letter_spacing: hlwLetterSpacing,
      locale,
      mobile: getDeviceIsMobile(),
      platform: "web",
      referrer: searchParams.get("referrer"),
      screen_height: isWindowExists ? window?.innerHeight : 0,
      /** TODO: define getScreenName() */
      screen_name: isWindowExists ? window?.location.pathname : "",
      screen_width: isWindowExists ? window?.innerWidth : 0,
      slug: `${pathname}${window.location.search}${window.location.hash}`,
      theme: preferredTheme,
      utm_campaign: searchParams.get("utmCampaign"),
      utm_medium: searchParams.get("utmMedium"),
      utm_source: searchParams.get("utmSource"),
      word_spacing: hlwWordSpacing,
      ...properties,
    }) as unknown as Promise<void>;
  };
  const backendEvent = useCallback(
    ({ event, body }) => {
      const eventUuid = uuid();
      const createdAt = new Date();
      return postEvent.mutateAsync({
        event,
        body,
        uuid: eventUuid,
        created_at: createdAt,
      });
    },
    [postEvent],
  );

  /** If the user is consenting, load analytics */
  useEffect(() => {
    if (isConsenting) {
      analytics.load(
        {
          writeKey: process.env.NEXT_PUBLIC_SEGMENT_WRITE_KEY,
        },
        {
          integrations: {
            "Segment.io": {
              apiHost: process.env.NEXT_PUBLIC_SEGMENT_API_HOST,
              protocol: "https",
            },
          },
        },
      );
      analytics.identify().then(() => {
        /**
         * Update context ajs_anonymoud_id cookie if not in-sync
         * To keep LD (guest context) id in sync with Segment id
         */
        const cookieAjsAnonymousId = getCookieAjsAnonymousId();
        if (ajsAnonymousId !== cookieAjsAnonymousId)
          setAjsAnonymousId(cookieAjsAnonymousId);
      });
    }
  }, [isConsenting]);

  /**
   * If user authenticates, sync pre-auth and post-auth analytics
   * Segment analytics.identify() syncs events
   */
  const isIdentifiable = isConsenting && !isIdentified && queryMe.data?.id;
  useEffect(() => {
    if (isIdentifiable) {
      analytics.identify(queryMe.data.id.toString(), {
        consent,
        email: queryMe.data?.email,
        locale: queryMe.data?.language,
        name: getFullName({
          firstName: queryMe.data?.name,
          lastName: queryMe.data?.last_name,
        }),
        phoneNumber: queryMe.data?.phone,
      });
      setIsIdentified(true);
    }
  }, [isIdentifiable]);

  return (
    <analyticsContext.Provider
      value={{
        analytics,
        logout,
        track,
        postEvent: backendEvent,
      }}
      {...props}
    >
      {children}
    </analyticsContext.Provider>
  );
};

export const AnalyticsProvider = ({
  children,
  ...props
}: AnalyticsProviderProps) => (
  <Suspense>
    <Provider {...props}>{children}</Provider>
  </Suspense>
);
